// A table's box: its column names stay in sight while its rows scroll
// (UX_STANDARDS UX-36), and it scrolls sideways only when it has to (UX-9).
//
// WHY THE SIDEWAYS SCROLL IS SWITCHED ON AND OFF. A sticky head sticks to the
// nearest box that clips its overflow, and a box that scrolls sideways clips
// up and down as well — so a table that could scroll sideways held its head
// to a box that never scrolls up or down, and the head left with the rows
// (My Team's table on a desktop, 2026-10-11). Every table on the site fits its
// box at the ordinary text size, at every width from 320 up, so the box
// scrolls sideways only while its table is wider than it — a large text size
// on a narrow screen — and the rest of the time the head sticks in CSS, at the
// speed of the scroll itself.
//
// WHILE IT DOES SCROLL SIDEWAYS, the head is moved down by hand, as far as the
// region above has scrolled past the table's top, so it stays in sight there
// too. A box that always scrolls sideways (a player's game log) is the same
// case.

import { useLayoutEffect, useRef, type ReactNode } from 'react';

/**
 * The nearest box that scrolls up and down: what a table's head stays at the
 * top of. One that scrolls only sideways reads as `auto` up and down too, and
 * scrolls nothing there, so a box whose rows overflow it comes first.
 */
export function scrollerOf(el: HTMLElement): HTMLElement | null {
  let first: HTMLElement | null = null;
  for (let a = el.parentElement; a; a = a.parentElement) {
    if (!/auto|scroll/.test(getComputedStyle(a).overflowY)) continue;
    if (a.scrollHeight > a.clientHeight + 1) return a;
    first ??= a;
  }
  return first;
}

/**
 * How far down to move a head row that cannot stick in CSS, because its box
 * scrolls sideways: as far as `scroller` has scrolled past the table's top,
 * under whatever sticks above it there, and never past its own table's end.
 * The board of a draft (`DraftBoardGrid`) is a grid rather than a `<table>`
 * and scrolls sideways always, so it moves its head the same way.
 */
export function headDrop(
  scroller: HTMLElement,
  box: HTMLElement,
  table: HTMLElement,
  head: HTMLElement,
): number {
  // Under whatever sticks above it, where the head would stick in CSS
  // (`--fsp-stick-top`): a phone's sticky row (UX-38), a sheet's title
  // bar, a card's pinned top. The stylesheet hands it over as the box's
  // `scroll-margin-top`, which comes back as a length where the property
  // itself may be a sum. It counts from inside the scroller's padding,
  // as a sticky offset does.
  const s = getComputedStyle(scroller);
  const stuck =
    parseFloat(s.borderTopWidth) +
    parseFloat(s.paddingTop) +
    (parseFloat(getComputedStyle(box).scrollMarginTop) || 0);
  const top = scroller.getBoundingClientRect().top + stuck;
  const t = table.getBoundingClientRect();
  return Math.max(0, Math.min(top - t.top, t.height - head.offsetHeight));
}

export function TableScroll({
  className,
  children,
}: {
  /** The box's own class: its border, its radius. */
  className?: string;
  /** One `<table>`, with a `<thead>`. */
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const box = ref.current;
    const table = box?.querySelector('table');
    if (!box || !table) return;
    const scroller = scrollerOf(box);
    let frame = 0;
    const follow = () => {
      frame = 0;
      const head = table.tHead;
      if (!head) return;
      if (!box.hasAttribute('data-sideways') || !scroller) {
        head.style.transform = '';
        return;
      }
      const by = headDrop(scroller, box, table, head);
      head.style.transform = by > 0 ? `translateY(${by}px)` : '';
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(follow);
    };
    const measure = () => {
      box.toggleAttribute('data-sideways', table.offsetWidth > box.clientWidth + 1);
      follow();
    };
    const sizes = new ResizeObserver(measure);
    sizes.observe(box);
    sizes.observe(table);
    scroller?.addEventListener('scroll', onScroll, { passive: true });
    measure();
    return () => {
      sizes.disconnect();
      scroller?.removeEventListener('scroll', onScroll);
      if (frame) cancelAnimationFrame(frame);
    };
  }, []);
  return (
    <div ref={ref} className={`fsp-table-scroll${className ? ` ${className}` : ''}`}>
      {children}
    </div>
  );
}
